import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Box, Chip, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import PowerSettingsNewRounded from "@mui/icons-material/PowerSettingsNewRounded";
import WelcomeHeader from "../components/WelcomeHeader";
import { getStoredAuthToken } from "../utils/authSession";

const providerMetadataByCode = {
  CV_CRM: {
    description: "Provedor para sincronizar leads, atendimentos e dados comerciais.",
    logo: { type: "image", src: "/logoCV-black.svg", alt: "CV-CRM" },
  },
  DOMMUS: {
    description: "Sistema disponível para integração com dados da operação.",
    logo: { type: "image", src: "/dommus-logo.svg", alt: "Dommus" },
  },
  FACILITA: {
    description: "Provedor para integrar informações comerciais e de relacionamento.",
    logo: { type: "icon" },
  },
  RD_STATION: {
    description: "Conecte automações de marketing, leads e oportunidades.",
    logo: { type: "image", src: "/rd-station-logo.svg", alt: "RD Station" },
  },
  MOSKIT: {
    description: "Integre dados de CRM, funil comercial e acompanhamento de vendas.",
    logo: { type: "image", src: "/moskit-logo.svg", alt: "Moskit" },
  },
  SIENGE: {
    description: "Provedor para dados financeiros, contratos e recebíveis.",
    logo: { type: "image", src: "/sienge-logo.png", alt: "Sienge" },
  },
  MEGA: {
    description: "Sistema disponível para integração com gestão e operação.",
    logo: { type: "image", src: "/mega-logo.svg", alt: "Senior Mega" },
  },
  INFORMAKON: {
    description: "ERP disponível para integração com gestão e operação.",
    logo: { type: "image", src: "/informakon-logo.svg", alt: "Informakon" },
  },
  INFORMACON: {
    description: "ERP disponível para integração com gestão e operação.",
    logo: { type: "image", src: "/informakon-logo.svg", alt: "Informakon" },
  },
  WHATSAPP_META: {
    description: "Integre canais oficiais de atendimento e comunicação pelo WhatsApp.",
    logo: { type: "image", src: "/whatsapp-meta-icon.svg", alt: "WhatsApp Oficial Meta" },
  },
  ASANA: {
    description: "Sincronize tarefas, projetos e fluxos de trabalho.",
    logo: { type: "image", src: "/asana-logo.svg", alt: "Asana" },
  },
  MICROSOFT_PLANNER: {
    description: "Conecte tarefas e planos do Microsoft Planner.",
    logo: { type: "image", src: "/microsoft-planner-logo.svg", alt: "Microsoft Planner" },
  },
  PLANNER: {
    description: "Conecte tarefas e planos do Microsoft Planner.",
    logo: { type: "image", src: "/microsoft-planner-logo.svg", alt: "Microsoft Planner" },
  },
  MS_PLANNER: {
    description: "Conecte tarefas e planos do Microsoft Planner.",
    logo: { type: "image", src: "/microsoft-planner-logo.svg", alt: "Microsoft Planner" },
  },
  CRM_AIPIM: {
    description: "CRM da Aipim disponível para integração com a jornada comercial.",
    logo: {
      type: "image",
      src: "/aipim-tech-logo.png",
      alt: "Aipim.tech",
      sx: {
        width: "100%",
        height: "100%",
        objectFit: "contain",
        borderRadius: 1.5,
        backgroundColor: "#fff",
        padding: "3px",
        boxSizing: "border-box",
      },
    },
  },
  TASKAGENT: {
    description: "Sincronize processos, tarefas e automações operacionais.",
    logo: { type: "icon" },
  },
};

const providerCategoryPriority = ["CRM", "ERP", "COMUNICACAO", "TAREFAS"];
const supportedProviderCategories = ["CRM", "ERP", "COMUNICACAO", "TAREFAS", "MARKETING", "OUTROS"];
const multiConfigurableProviderCodes = new Set(["WHATSAPP_META"]);
const providerCategoryLabels = {
  CRM: "CRM",
  ERP: "ERP",
  COMUNICACAO: "COMUNICAÇÃO",
  TAREFAS: "TAREFAS",
  MARKETING: "MARKETING",
  OUTROS: "OUTROS",
};

const statusLabels = {
  ATIVA: "Ativa",
  INATIVA: "Inativa",
  ERRO: "Erro",
  PENDENTE: "Pendente",
};

const normalizeToken = (token) => (token?.startsWith("Bearer ") ? token.slice(7) : token);

const normalizeProviderCategory = (category) => {
  const normalized = String(category || "OUTROS")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  return supportedProviderCategories.includes(normalized) ? normalized : "OUTROS";
};

const getProviderId = (provider) =>
  String(provider?.codigo || provider?.nome || provider?.id || "")
    .toLowerCase()
    .replace(/_/g, "-")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "");

const formatStatusLabel = (status) => {
  const normalized = String(status || "").trim().toUpperCase();
  return statusLabels[normalized] || normalized || "Sem status";
};

const formatDateTime = (value) => {
  if (!value) return "";

  try {
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (error) {
    return "";
  }
};

const getCategoryPriorityIndex = (category) => {
  const normalizedCategory = normalizeProviderCategory(category);
  const index = providerCategoryPriority.indexOf(normalizedCategory);

  return index === -1 ? providerCategoryPriority.length : index;
};

const getProviderPriorityIndex = (provider) => {
  const category = normalizeProviderCategory(provider?.category);
  const code = String(provider?.code || "").toUpperCase();

  if (category === "CRM" && code === "CV_CRM") return 0;
  if (category === "ERP" && code === "SIENGE") return 0;

  return 1;
};

const sortProvidersByBusinessPriority = (currentProvider, nextProvider) => {
  const prioritySort = getProviderPriorityIndex(currentProvider) - getProviderPriorityIndex(nextProvider);

  if (prioritySort !== 0) return prioritySort;

  return String(currentProvider?.name || "").localeCompare(String(nextProvider?.name || ""), "pt-BR");
};

const sortClientIntegrationsByBusinessPriority = (currentIntegration, nextIntegration) => {
  const categorySort =
    getCategoryPriorityIndex(currentIntegration.provider.category) -
    getCategoryPriorityIndex(nextIntegration.provider.category);

  if (categorySort !== 0) return categorySort;

  return sortProvidersByBusinessPriority(currentIntegration.provider, nextIntegration.provider);
};

const buildProviderViewModel = (provider) => {
  const code = String(provider?.codigo || "").toUpperCase();
  const name = provider?.nome || code || "Provedor";
  const normalizedName = String(name)
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  const metadata =
    providerMetadataByCode[code] ||
    (normalizedName.includes("MICROSOFT") && normalizedName.includes("PLANNER")
      ? providerMetadataByCode.MICROSOFT_PLANNER
      : {});
  const category = normalizeProviderCategory(provider?.categoria);

  return {
    id: getProviderId(provider),
    providerId: provider?.id,
    code,
    name,
    category,
    active: Boolean(provider?.ativo),
    description: metadata.description || `Provedor ${providerCategoryLabels[category].toLowerCase()} disponível para integração.`,
    logo: metadata.logo || { type: "icon" },
  };
};

const markProviderConfigurationState = (provider, configuredProviderCodes) => ({
  ...provider,
  isConfigured: configuredProviderCodes.has(provider.code),
});

const buildClientIntegrationViewModel = (clientIntegration) => {
  const provider = buildProviderViewModel(clientIntegration?.provedor || {});
  const status = String(clientIntegration?.status || "").toUpperCase();
  const isActive = status === "ATIVA";
  const lastTest = formatDateTime(clientIntegration?.ultimoTesteConexaoEm);

  return {
    id: clientIntegration?.id,
    provider,
    name: clientIntegration?.nomeExibicao || provider.name,
    status,
    statusLabel: formatStatusLabel(status),
    isActive,
    lastTest,
    errorMessage: clientIntegration?.ultimaMensagemErro || "",
  };
};

const LogoMark = ({ integration }) => {
  const [imageError, setImageError] = useState(false);

  if (integration.logo?.fallbackText && imageError) {
    return (
      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 900,
          color: integration.id === "sienge" ? "#1f9bd7" : "text.primary",
          lineHeight: 1,
        }}
      >
        {integration.logo.fallbackText}
      </Typography>
    );
  }

  if (integration.logo?.type === "image") {
    return (
      <Box
        component="img"
        src={integration.logo.src}
        alt={integration.logo.alt}
        onError={() => setImageError(true)}
        sx={{
          maxWidth: "100%",
          maxHeight: 34,
          objectFit: "contain",
          filter: (theme) =>
            integration.id === "cv-crm" && theme.palette.mode === "dark"
              ? "brightness(0) invert(1)"
              : "none",
          ...integration.logo.sx,
        }}
      />
    );
  }

  return <AccountTreeOutlined sx={{ fontSize: 32, color: "primary.main" }} />;
};

const InlineSectionHeader = ({ title, description }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    spacing={{ xs: 0.3, sm: 1.2 }}
    alignItems={{ xs: "flex-start", sm: "baseline" }}
  >
    <Typography
      variant="overline"
      sx={{
        color: "text.secondary",
        fontSize: 14,
        fontWeight: 900,
        lineHeight: 1.4,
      }}
    >
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {description}
    </Typography>
  </Stack>
);

const Integracoes = () => {
  const [clientIntegrations, setClientIntegrations] = useState([]);
  const [clientIntegrationsLoading, setClientIntegrationsLoading] = useState(true);
  const [clientIntegrationsError, setClientIntegrationsError] = useState("");
  const [providers, setProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [providersError, setProvidersError] = useState("");
  const baseURL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

  const providersByCategory = useMemo(
    () => {
      const configuredProviderCodes = new Set(
        clientIntegrations.map((integration) => integration.provider.code).filter(Boolean)
      );

      const visibleProviders = providers.map((provider) =>
        markProviderConfigurationState(provider, configuredProviderCodes)
      );

      const groupedProviders = visibleProviders.reduce((currentGroupedProviders, provider) => {
        const category = normalizeProviderCategory(provider.category);
        currentGroupedProviders[category] = currentGroupedProviders[category] || [];
        currentGroupedProviders[category].push(provider);
        return currentGroupedProviders;
      }, {});

      Object.keys(groupedProviders).forEach((category) => {
        groupedProviders[category].sort(sortProvidersByBusinessPriority);
      });

      return groupedProviders;
    },
    [clientIntegrations, providers]
  );

  const visibleProviderCategories = useMemo(
    () => {
      const existingCategories = Object.keys(providersByCategory);
      const priorityCategories = providerCategoryPriority.filter((category) => providersByCategory[category]?.length);
      const remainingCategories = existingCategories.filter((category) => !providerCategoryPriority.includes(category));

      return [...priorityCategories, ...remainingCategories];
    },
    [providersByCategory]
  );

  useEffect(() => {
    let isMounted = true;

    const fetchIntegrationsData = async () => {
      const token = normalizeToken(getStoredAuthToken());

      if (!token) {
        if (isMounted) {
          setClientIntegrations([]);
          setProviders([]);
          setClientIntegrationsError("Token de autenticação não encontrado.");
          setProvidersError("Token de autenticação não encontrado.");
          setClientIntegrationsLoading(false);
          setProvidersLoading(false);
        }
        return;
      }

      setClientIntegrationsLoading(true);
      setProvidersLoading(true);
      setClientIntegrationsError("");
      setProvidersError("");

      const headers = { Authorization: `Bearer ${token}` };

      const clientIntegrationsRequest = axios.get(`${baseURL}/integracoes/integracoes_cliente`, {
        headers,
      });
      const providersRequest = axios.get(`${baseURL}/integracoes/provedores`, { headers });

      const [clientIntegrationsResult, providersResult] = await Promise.allSettled([
        clientIntegrationsRequest,
        providersRequest,
      ]);

      if (!isMounted) return;

  if (clientIntegrationsResult.status === "fulfilled") {
        const nextClientIntegrations = Array.isArray(clientIntegrationsResult.value.data)
          ? clientIntegrationsResult.value.data
              .map(buildClientIntegrationViewModel)
              .sort(sortClientIntegrationsByBusinessPriority)
          : [];
        setClientIntegrations(nextClientIntegrations);
      } else {
        console.error("Erro ao buscar integrações do cliente:", clientIntegrationsResult.reason);
        setClientIntegrations([]);
        setClientIntegrationsError("Não foi possível carregar as integrações configuradas.");
      }

      if (providersResult.status === "fulfilled") {
        const nextProviders = Array.isArray(providersResult.value.data)
          ? providersResult.value.data.filter((provider) => provider?.ativo !== false).map(buildProviderViewModel)
          : [];
        setProviders(nextProviders);
      } else {
        console.error("Erro ao buscar provedores de integração:", providersResult.reason);
        setProviders([]);
        setProvidersError("Não foi possível carregar os provedores de integração.");
      }

      setClientIntegrationsLoading(false);
      setProvidersLoading(false);
    };

    fetchIntegrationsData();

    return () => {
      isMounted = false;
    };
  }, [baseURL]);

  return (
    <Stack spacing={3}>
      <WelcomeHeader title="Integrações" subtitle="" />

      <InlineSectionHeader
        title="Integrações configuradas"
        description="Conexões ativas ou cadastradas"
      />

      {clientIntegrationsLoading ? (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={22} />
          <Typography color="text.secondary">Carregando integrações configuradas...</Typography>
        </Stack>
      ) : clientIntegrationsError ? (
        <Alert severity="error">{clientIntegrationsError}</Alert>
      ) : clientIntegrations.length === 0 ? (
        <Alert severity="info">Nenhuma integração configurada para este cliente.</Alert>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: 2,
          }}
        >
          {clientIntegrations.map((integration) => (
            <Paper
              key={integration.id || `${integration.provider.code}-${integration.name}`}
              elevation={0}
              sx={(theme) => ({
                minHeight: 148,
                p: 1.8,
                borderRadius: 2,
                border: `1px solid ${
                  integration.isActive ? "rgba(46, 125, 50, 0.34)" : theme.palette.divider
                }`,
                bgcolor: "background.paper",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
                "&:hover": {
                  borderColor: integration.isActive ? "success.main" : "primary.main",
                  boxShadow:
                    theme.palette.mode === "dark"
                      ? "0 14px 28px rgba(0, 0, 0, 0.25)"
                      : "0 14px 28px rgba(16, 24, 40, 0.08)",
                  transform: "translateY(-1px)",
                },
              })}
            >
              <Stack spacing={1.6}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Box
                    sx={(theme) => ({
                      width: 102,
                      height: 46,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: theme.palette.mode === "dark" ? "rgba(15, 23, 42, 0.7)" : "#f8fafc",
                      border: `1px solid ${theme.palette.divider}`,
                      overflow: "hidden",
                    })}
                  >
                    <LogoMark integration={integration.provider} />
                  </Box>
                  <Chip
                    icon={integration.isActive ? <CheckCircleRounded /> : <PowerSettingsNewRounded />}
                    label={integration.statusLabel}
                    size="small"
                    sx={{
                      fontWeight: 800,
                      bgcolor: integration.isActive ? "#e8f5e8" : "rgba(148, 163, 184, 0.14)",
                      color: integration.isActive ? "#2e7d32" : "text.secondary",
                      "& .MuiChip-icon": {
                        color: integration.isActive ? "#2e7d32" : "text.secondary",
                      },
                    }}
                  />
                </Stack>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {integration.name}
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={0.4} sx={{ mt: 1.4 }}>
                {integration.lastTest ? (
                  <Typography variant="body2" color="text.secondary">
                    Último teste: {integration.lastTest}
                  </Typography>
                ) : null}
                {integration.errorMessage ? (
                  <Typography variant="body2" color="error.main">
                    {integration.errorMessage}
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
          ))}
        </Box>
      )}

      <InlineSectionHeader
        title="Provedores disponíveis"
        description="Sistemas que podem ser conectados à operação"
      />

      {providersLoading ? (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={22} />
          <Typography color="text.secondary">Carregando provedores...</Typography>
        </Stack>
      ) : providersError ? (
        <Alert severity="error">{providersError}</Alert>
      ) : visibleProviderCategories.length === 0 ? (
        <Alert severity="info">Nenhum provedor de integração disponível no momento.</Alert>
      ) : (
        <Stack spacing={3}>
          {visibleProviderCategories.map((category) => {
            const categoryProviders = providersByCategory[category] || [];

            return (
              <Box key={category}>
                <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 900 }}>
                  {providerCategoryLabels[category] || category}
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                    gap: 2,
                    mt: 1,
                  }}
                >
                  {categoryProviders.map((provider) => (
                    <Paper
                      key={`${provider.code}-${provider.providerId || provider.id}`}
                      elevation={0}
                      sx={(theme) => ({
                        position: "relative",
                        overflow: "hidden",
                        minHeight: 128,
                        p: 1.4,
                        borderRadius: 2,
                        border: `1px solid ${
                          provider.isConfigured && !multiConfigurableProviderCodes.has(provider.code)
                            ? "rgba(46, 125, 50, 0.34)"
                            : theme.palette.divider
                        }`,
                        bgcolor: "background.paper",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
                        "&:hover": {
                          borderColor: "primary.main",
                          boxShadow:
                            theme.palette.mode === "dark"
                              ? "0 14px 28px rgba(0, 0, 0, 0.25)"
                              : "0 14px 28px rgba(16, 24, 40, 0.08)",
                          transform: "translateY(-1px)",
                        },
                      })}
                    >
                      {provider.isConfigured && !multiConfigurableProviderCodes.has(provider.code) ? (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 10,
                            right: -38,
                            width: 124,
                            py: 0.25,
                            textAlign: "center",
                            transform: "rotate(35deg)",
                            bgcolor: "success.main",
                            color: "success.contrastText",
                            fontSize: 10,
                            fontWeight: 900,
                            letterSpacing: 0,
                            pointerEvents: "none",
                          }}
                        >
                          Integrado
                        </Box>
                      ) : null}

                      <Stack spacing={1.4}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                          <Box
                            sx={(theme) => ({
                              width: 88,
                              height: 38,
                              borderRadius: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: theme.palette.mode === "dark" ? "rgba(15, 23, 42, 0.7)" : "#f8fafc",
                              border: `1px solid ${theme.palette.divider}`,
                              overflow: "hidden",
                            })}
                          >
                            <LogoMark integration={provider} />
                          </Box>
                          <Chip
                            label={
                              provider.isConfigured && !multiConfigurableProviderCodes.has(provider.code)
                                ? "Integrado"
                                : "Disponível"
                            }
                            size="small"
                            sx={{
                              fontWeight: 800,
                              bgcolor:
                                provider.isConfigured && !multiConfigurableProviderCodes.has(provider.code)
                                  ? "#e8f5e8"
                                  : "rgba(25, 118, 210, 0.1)",
                              color:
                                provider.isConfigured && !multiConfigurableProviderCodes.has(provider.code)
                                  ? "#2e7d32"
                                  : "primary.main",
                            }}
                          />
                        </Stack>

                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                            {provider.name}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default Integracoes;
